/* istanbul ignore file */

import { DOCUMENT } from '@angular/common';
import { inject, Injectable } from '@angular/core';
import { NavigationEnd, NavigationError, Router } from '@angular/router';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { filter, fromEvent, map, skip, switchMap, take, throttleTime } from 'rxjs';

import { AuthService } from '../../common/auth/auth.service';
import { LogoutAdaptor } from '../../features/auth/shared/logout.adaptor';
import { ProfileFetchAdaptor } from '../profile/profile.adaptor';

enum Events {
  USER_LOGGED_IN = 'USER_LOGGED_IN',
  USER_LOGGED_OUT = 'USER_LOGGED_OUT',
  ELEMENT_CLICKED = 'ELEMENT_CLICKED',
  CONTENT_SCROLLED = 'CONTENT_SCROLLED',
  PAGE_ENTERED = 'PAGE_ENTERED',
  PAGE_EXITED = 'PAGE_EXITED',
  LINK_NAVIGATED = 'LINK_NAVIGATED',
  PAGE_ERRORED = 'PAGE_ERRORED',
  PAGE_LEFT = 'PAGE_LEFT',
  SEARCH_EXECUTED = 'SEARCH_EXECUTED',
  FILTER_SET = 'FILTER_SET',
  FILTER_CLEARED = 'FILTER_CLEARED',
  INSTANT_FEEDBACK = 'INSTANT_FEEDBACK',
  // Custom tracking events for forms and user actions
  FORM_STEP_STARTED = 'FORM_STEP_STARTED',
  FORM_STEP_COMPLETED = 'FORM_STEP_COMPLETED',
  FORM_ABANDONED = 'FORM_ABANDONED',
  FORM_SUBMITTED = 'FORM_SUBMITTED',
  FILE_DOWNLOADED = 'FILE_DOWNLOADED',
  REFERRAL_LINK_SHARED = 'REFERRAL_LINK_SHARED',
  CONTACT_REQUEST_SUBMITTED = 'CONTACT_REQUEST_SUBMITTED',
  SESSION_LINKED = 'SESSION_LINKED',
}

const LOGOUT_REASONS = {
  logout: 'logout-button',
  guard: 'expired',
  undefined: 'unknown',
};

@UntilDestroy()
@Injectable({ providedIn: 'root' })
export class AnalyticsService {
  private auth = inject(AuthService);
  private profile = inject(ProfileFetchAdaptor);
  private logout = inject(LogoutAdaptor);
  private router = inject(Router);

  private document = inject(DOCUMENT);

  _id: unknown;
  _previousUrl: unknown;
  _sessionId: string;
  _ipAddress: string | null = null;

  constructor() {
    // Generate or retrieve session ID
    this._sessionId = this.getOrCreateSessionId();

    // set user id
    this.profile.data$
      .pipe(
        filter((token) => token !== null),
        untilDestroyed(this),
      )
      .subscribe((profile) => {
        this.setUser(profile.id);
        this._id = profile.id;
      });

    // when user logs out
    this.logout.logout$.pipe(untilDestroyed(this)).subscribe((source) => {
      this.log(Events.USER_LOGGED_OUT, { reason: LOGOUT_REASONS[source as keyof typeof LOGOUT_REASONS] });
      this._id = null;
    });

    // when user logs in
    // Ensure token is null -> wait for login token -> wait for profile data once
    this.auth.token$
      .pipe(
        filter((token) => token === null),
        switchMap(() =>
          this.auth.token$.pipe(
            skip(1),
            switchMap(() => this.profile.data$.pipe(filter((token) => token !== null))),
            take(1),
          ),
        ),
        untilDestroyed(this),
      )
      .subscribe((profile) => {
        // Log login event
        this.log(Events.USER_LOGGED_IN, {});

        // Log session linkage event for backend analysis
        // This explicitly links the current session to the user who just logged in
        this.log(Events.SESSION_LINKED, {
          user_id: profile.id,
          previous_session_id: this._sessionId,
          linked_at: new Date().toISOString(),
        });
      });

    // click events and link navigation
    fromEvent<MouseEvent>(this.document, 'click')
      .pipe(
        map((event) => {
          const elm = event.target as HTMLElement;
          return {
            event,
            element: elm.closest('a,button,ion-button,[routerlink],ion-accordion>ion-item') as HTMLElement,
          };
        }),
        filter(({ element }) => {
          return (
            element !== null &&
            // edge case: ion-button with type submit creates a another button with submit type to simulate actual submit event
            !(element.getAttribute('type') === 'submit' && element.style.display === 'none')
          );
        }),
        // Throttle to prevent rapid duplicate clicks (300ms window)
        // Uses leading edge emission, so first click in window is immediately tracked
        throttleTime(300),
        untilDestroyed(this),
      )
      .subscribe(({ element, event }) => {
        let label = element.title || element.ariaLabel || element.textContent?.trim();

        // ion-button
        const shadowElm = element.shadowRoot?.querySelector<HTMLElement>('[title],[aria-label]');
        if (shadowElm != null) {
          label = shadowElm.title || shadowElm.ariaLabel || label;
        }

        this.log(Events.ELEMENT_CLICKED, {
          label,
          element: element.tagName,
          mouseButton: event.button, // 0=left, 1=middle, 2=right
        });

        const href = (element as HTMLAnchorElement).href;
        if (href && (href.startsWith('//') || href.startsWith('http'))) {
          // Calculate link ordinal if it's within a list
          let linkOrdinal: number | null = null;
          const listItem = element.closest('li, ion-item');
          if (listItem && listItem.parentElement) {
            const siblings = Array.from(listItem.parentElement.children);
            linkOrdinal = siblings.indexOf(listItem) + 1; // 1-based index
          }

          this.log(Events.LINK_NAVIGATED, {
            label,
            url: href,
            linkOrdinal,
          });
        }
      });

    // navigation events
    this.router.events.pipe(untilDestroyed(this)).subscribe((event) => {
      if (event instanceof NavigationEnd) {
        if (this._previousUrl) {
          this.log(Events.PAGE_EXITED, { url: this._previousUrl });
        }
        this.log(Events.PAGE_ENTERED, {
          url: event.url,
          userAgent: navigator.userAgent,
        });
        this._previousUrl = event.url;
      } else if (event instanceof NavigationError) {
        this.log(Events.PAGE_ERRORED, { errorType: 'Navigation Error', error: event.error, url: event.url });
      }
    });

    window.addEventListener(
      'beforeunload',
      () => {
        this.log(Events.PAGE_EXITED, { url: this._previousUrl });
        this.log(Events.PAGE_LEFT, { url: this._previousUrl });
      },
      true,
    );
  }

  setUser(id: unknown) {
    window.gtag('set', 'user_id', id);
  }

  scroll(
    scrollTop: number,
    pageUrl: string,
    metrics: {
      scrollPercentage: number;
      verticalScrollProportion: number;
      onscreenContentProportion: number;
      scrollDistance: number;
      maxScrollReached: number;
    },
  ) {
    this.log(Events.CONTENT_SCROLLED, {
      scrollTop,
      pageUrl,
      scrollPercentage: metrics.scrollPercentage,
      verticalScrollProportion: metrics.verticalScrollProportion,
      onscreenContentProportion: metrics.onscreenContentProportion,
      scrollDistance: metrics.scrollDistance,
      maxScrollReached: metrics.maxScrollReached,
    });
  }

  error(type: string, error: unknown) {
    this.log(Events.PAGE_ERRORED, { errorType: type, error });
  }

  getParams(event: Events, meta: Record<string, unknown>) {
    return {
      UserKey: this._id || this._sessionId, // Use session ID if user not logged in
      SessionID: this._sessionId,
      IPAddress: this._ipAddress,
      OccurredAt: new Date().toISOString(),
      Component: 'yss-frontend',
      Source: window.location.pathname,
      EventType: event,
      EventMetadata: JSON.stringify(meta),
    };
  }

  log(event: Events, opt: Record<string, unknown>) {
    window.dataLayer.push({ event, ...this.getParams(event, opt) });
  }

  // High Priority Implementations
  trackUserLoggedIn(userId: string) {
    this.log(Events.USER_LOGGED_IN, {
      user_id: userId,
      timestamp: new Date().toISOString(),
    });
  }

  trackUserLoggedOut() {
    this.log(Events.USER_LOGGED_OUT, {
      timestamp: new Date().toISOString(),
    });
  }

  trackPageExited(pageTitle: string) {
    this.log(Events.PAGE_EXITED, {
      page_title: pageTitle,
      timestamp: new Date().toISOString(),
    });
  }

  trackSearch(searchString: string, resultCount: number) {
    this.log(Events.SEARCH_EXECUTED, {
      search_string: searchString,
      result_count: resultCount,
    });
  }

  trackFilterSet(filterSpec: Record<string, unknown>, resultCount: number) {
    this.log(Events.FILTER_SET, {
      filter_specification: filterSpec,
      result_count: resultCount,
    });
  }

  trackFilterCleared() {
    this.log(Events.FILTER_CLEARED, {
      timestamp: new Date().toISOString(),
    });
  }

  // Custom tracking methods for forms and user actions

  /**
   * Track when a user starts a form step
   * @param formName - Name of the form (e.g., 'online_referral_form', 'unmet_needs_survey')
   * @param stepName - Name of the step (e.g., 'Patient Details', 'Section 1')
   * @param stepIndex - Zero-based index of the step
   */
  trackFormStepStarted(formName: string, stepName: string, stepIndex: number) {
    this.log(Events.FORM_STEP_STARTED, {
      form_name: formName,
      step_name: stepName,
      step_index: stepIndex,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track when a user completes a form step
   * @param formName - Name of the form
   * @param stepName - Name of the step
   * @param stepIndex - Zero-based index of the step
   * @param timeSpent - Time spent on this step in seconds
   */
  trackFormStepCompleted(formName: string, stepName: string, stepIndex: number, timeSpent: number) {
    this.log(Events.FORM_STEP_COMPLETED, {
      form_name: formName,
      step_name: stepName,
      step_index: stepIndex,
      time_spent_seconds: timeSpent,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track when a user abandons a form
   * @param formName - Name of the form
   * @param stepName - Name of the step where they abandoned
   * @param stepIndex - Zero-based index of the step where they abandoned
   * @param completedSteps - Number of steps completed
   * @param totalSteps - Total number of steps in the form
   */
  trackFormAbandoned(
    formName: string,
    stepName: string,
    stepIndex: number,
    completedSteps: number,
    totalSteps: number,
  ) {
    this.log(Events.FORM_ABANDONED, {
      form_name: formName,
      step_name: stepName,
      step_index: stepIndex,
      completed_steps: completedSteps,
      total_steps: totalSteps,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track file downloads
   * @param fileName - Name of the downloaded file
   * @param fileType - File extension/type (e.g., 'pdf', 'docx')
   * @param downloadMethod - How the file was downloaded (e.g., 'direct_download', 'link_click')
   */
  trackFileDownload(fileName: string, fileType: string, downloadMethod: string) {
    this.log(Events.FILE_DOWNLOADED, {
      file_name: fileName,
      file_type: fileType,
      download_method: downloadMethod,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track when a referral link is shared
   * @param recipientType - Type of recipient (e.g., 'health_professional', 'gp')
   * @param onBehalfOf - Whether the share is on behalf of someone else
   */
  trackReferralLinkShared(recipientType: string, onBehalfOf: boolean) {
    this.log(Events.REFERRAL_LINK_SHARED, {
      recipient_type: recipientType,
      on_behalf_of: onBehalfOf,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track contact form submissions
   * @param contactPreference - User's preferred contact method ('call', 'email')
   * @param hasEmail - Whether email was provided
   * @param hasPhone - Whether phone number was provided
   */
  trackContactRequest(contactPreference: string, hasEmail: boolean, hasPhone: boolean) {
    this.log(Events.CONTACT_REQUEST_SUBMITTED, {
      contact_preference: contactPreference,
      has_email: hasEmail,
      has_phone: hasPhone,
      timestamp: new Date().toISOString(),
    });
  }

  /**
   * Track form submission completion
   * @param formName - Name of the form (e.g., 'online_referral_form', 'contact_us')
   * @param success - Whether the submission was successful
   * @param errorMessage - Optional error message if submission failed
   */
  trackFormSubmitted(formName: string, success: boolean, errorMessage?: string) {
    this.log(Events.FORM_SUBMITTED, {
      form_name: formName,
      success,
      error_message: errorMessage,
      timestamp: new Date().toISOString(),
    });
  }

  // Helper method to generate or retrieve session ID
  private getOrCreateSessionId(): string {
    const SESSION_KEY = 'yss_analytics_session_id';
    let sessionId = sessionStorage.getItem(SESSION_KEY);

    if (!sessionId) {
      // Generate a new session ID
      sessionId = `${Date.now()}-${Math.random().toString(36).substring(2, 15)}`;
      sessionStorage.setItem(SESSION_KEY, sessionId);
    }

    return sessionId;
  }
}
